import { EyeOutlined } from '@ant-design/icons';
import { Anchor } from 'antd';
import React from 'react';
import { NavLink } from 'react-router-dom';
import useDynamicRefs from '../../../../hooks/use_dynamic_refs';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useResponsive } from '../../../../hooks/use_responsive';
import { OmniaUrls } from '../../../../lib/urls';
import { ClickKey_LakePage, WebAnalytics } from '../../../../lib/web_analytics';
import { WaterbodyPaths } from '../../../../routes';
import { ContentWrapper } from '../../../content_wrapper/content_wrapper';
import { MOBILE_HEADER_HEIGHT_APPROX } from '../../../header/header_mobile';
import SvgFishingReport from '../../../svg/fishing_report';
import SvgTechniques from '../../../svg/new_product_transparent';
import SvgProducts from '../../../svg/products';
import { SvgWaterTemp } from '../../../svg/water_temp';
import { LakePageIds, WaterbodyDetailQueryParams } from '../../waterbody_detail';
import styles from './waterbody_detail_nav.less';

interface WaterbodyDetailNavProps {
  hasCurrentConditions: boolean;
  hasRelevantOmniaVideo: boolean;
  hasWaterbodyProducts: boolean;
  totalWaterbodyFishingReports: number;
  urlSlug: string;
}

export const WATERBODY_DETAIL_NAV_HEIGHT_APPROX = 64;

export const WaterbodyDetailNav = (props: WaterbodyDetailNavProps) => {
  const {
    hasCurrentConditions,
    hasRelevantOmniaVideo,
    hasWaterbodyProducts,
    totalWaterbodyFishingReports,
    urlSlug,
  } = props;

  const { getCurrentQuery, currentQuery, stringify } = useQueryString();
  const _currentQuery = getCurrentQuery<WaterbodyDetailQueryParams>();

  const currentQueryStringified = '?' + stringify(currentQuery);

  const webAnalyticsLakeClickArgs = {
    season: _currentQuery.season_group,
    species: _currentQuery.species,
    technique: _currentQuery.technique,
    waterbody_slug: urlSlug,
  };

  const { getRef, setRef } = useDynamicRefs();
  const { isMobile } = useResponsive();
  const webAnalyticsLakePageClick = (type: ClickKey_LakePage) => {
    return () => WebAnalytics.lakePageClick(type, webAnalyticsLakeClickArgs as any);
  };

  return (
    <div className={styles.fullWidthBackground}>
      <ContentWrapper className={styles.contentWrapper}>
        <Anchor
          affix={false}
          key={`${hasRelevantOmniaVideo} ${hasWaterbodyProducts}`}
          targetOffset={200}
          showInkInFixed={false}
          className={styles.anchor}
          offsetTop={isMobile ? MOBILE_HEADER_HEIGHT_APPROX : 0}
          onChange={(currentActiveLink) => {
            if (currentActiveLink) {
              const spanRef = getRef<HTMLSpanElement>(currentActiveLink.replace('#', ''));
              const wrapper = getRef<any>('wrapper')?.current;
              if (wrapper && spanRef) {
                wrapper.scrollTo({
                  left: spanRef.current.getBoundingClientRect().left + wrapper.scrollLeft - 18,
                  behavior: 'smooth',
                });
              }
            }
          }}
        >
          <ul className={styles.navContainer} ref={setRef('wrapper')}>
            <li>
              <Anchor.Link
                href={`#${LakePageIds.overview}`}
                title={
                  <span
                    ref={setRef(LakePageIds.overview)}
                    onClick={webAnalyticsLakePageClick('[pg_navigation].(left_nav)')}
                  >
                    <span role="img" aria-label="eye-icon" className={styles.overviewIcon}>
                      <EyeOutlined />
                    </span>{' '}
                    Overview
                  </span>
                }
              />
            </li>
            {hasCurrentConditions && (
              <li>
                <NavLink
                  className={styles.navLink}
                  to={
                    OmniaUrls.waterbody({ url_slug: urlSlug }, WaterbodyPaths.CURRENT_CONDITIONS) +
                    currentQueryStringified
                  }
                >
                  <span
                    role="img"
                    aria-label="current-conditions-icon"
                    className={styles.currentConditionsIcon}
                  >
                    <SvgWaterTemp />
                  </span>
                  Current&nbsp;Conditions
                </NavLink>
              </li>
            )}
            {hasWaterbodyProducts && (
              <li>
                <Anchor.Link
                  href={`#${LakePageIds.technique_products}`}
                  title={
                    <span
                      ref={setRef(LakePageIds.technique_products)}
                      onClick={webAnalyticsLakePageClick('[pg_navigation].(left_nav)')}
                    >
                      <span role="img" aria-label="product-icon" className={styles.productIcon}>
                        <SvgProducts width={'1em'} height={'1em'} />
                      </span>
                      Products
                    </span>
                  }
                />
              </li>
            )}

            <li className={styles.fishingReportsLinks}>
              <Anchor.Link
                href={`#${LakePageIds.fishing_reports}`}
                title={
                  <span
                    ref={setRef(LakePageIds.fishing_reports)}
                    onClick={webAnalyticsLakePageClick('[pg_navigation].(left_nav)')}
                  >
                    <span
                      role="img"
                      aria-label="fishing-reports-icon"
                      className={styles.fishingReportsIcon}
                    >
                      <SvgFishingReport />
                    </span>
                    Fishing Reports ({totalWaterbodyFishingReports})
                  </span>
                }
              />
            </li>

            <li>
              <Anchor.Link
                href={`#${LakePageIds.top_techniques}`}
                title={
                  <span
                    ref={setRef(LakePageIds.top_techniques)}
                    onClick={webAnalyticsLakePageClick('[pg_navigation].(left_nav)')}
                  >
                    <span role="img" aria-label="technique-icon" className={styles.techniqueIcon}>
                      <SvgTechniques width={'1em'} height={'1em'} />
                    </span>
                    Techniques
                  </span>
                }
              />
            </li>
          </ul>
        </Anchor>
      </ContentWrapper>
    </div>
  );
};
